import { call, fork, put, select } from 'redux-saga/effects';
import { takeEvery } from 'redux-saga';
import {
  createAccountSuccess,
  createAccountFailure,
  setEmailErrorText,
  setMissingErrorText,
  setPasswordErrorText,
  setPhoneNumberErrorText,
  setEulaLink,
  setEulaLinkSuccess,
  setEulaLinkFailure,
  setCountry,
  setCountryFailure,
  setPrivacyPolicy
} from 'actions/registrationActions';
import {
  createAccountRequest,
  eulaRequest,
  locationRequest,
  privacyPolicyRequest,
  agreementDocumentRequest
} from 'requesters/registrationRequester';
import { loginRequest, clearCookiesRequest } from 'requesters/loginRequester';
import { redirectUser } from 'sagas/redirectionSaga';
import {
  CREATE_ACCOUNT_REQUEST,
  SET_COUNTRY_REQUEST,
  SET_COUNTRY_BY_LOCATION_API
} from 'constants/actionTypes';
import { registrationSuccess } from 'utils/analytics/registrationAnalytics';
import checkValidEmail from 'utils/checkValidEmail';
import checkValidPassword from 'utils/checkValidPassword';
import checkValidPhoneNumber from 'utils/checkValidPhoneNumber';
import checkInputErrors from 'utils/checkInputErrors';
import errorHandler from 'utils/errorHandler';

/**
 * Check for missing inputs required from registration page
 * @param {String} email
 * @param {String} firstName
 * @param {Boolean} isEulaAccepted
 * @param {String} lastName
 * @param {String} password
 * @param {String} phoneNumber
 * @return {Array} missingInputsLists
 */
function checkForMissingRegistrationInputs(
  email,
  firstName,
  isEulaAccepted,
  lastName,
  password,
  phoneNumber
) {
  let missingInputsList = [];

  if (!email) {
    missingInputsList.push('email');
  }
  if (!isEulaAccepted) {
    missingInputsList.push('eula');
  }
  if (!firstName) {
    missingInputsList.push('firstName');
  }
  if (!lastName) {
    missingInputsList.push('lastName');
  }
  if (!password) {
    missingInputsList.push('password');
  }
  if (!phoneNumber) {
    missingInputsList.push('phoneNumber');
  }

  return missingInputsList;
}

/* eslint-enable max-len, lines-around-comment */

/**
 * Create Account API call.
 * On success, handle rerouting the user correctly
 * On failure, dispatch createAccountFailure with the response error code
 * @param {String} email
 * @param {String} firstName
 * @param {Boolean} isEulaAccepted
 * @param {String} lastName
 * @param {String} password
 * @param {String} phoneNumber
 * @param {String} eulaId
 * @param {String} privacyPolicyId
 * @param {String} country
 * @param {String} countryCode
 * @return {void}
 */
export function *createAccount(
  email, firstName, isEulaAccepted, lastName, password, phoneNumber,
  eulaId, privacyPolicyId, country, countryCode
) {
  // Check for missing/empty required inputs
  const missingInputsList = checkForMissingRegistrationInputs(
    email,
    firstName,
    isEulaAccepted,
    lastName,
    password,
    phoneNumber
  );
  yield put(setMissingErrorText(missingInputsList));

  // Check if email is valid
  const emailErrorText = checkValidEmail(email);
  if (emailErrorText) {
    yield put(setEmailErrorText(emailErrorText));
  }

  // Check if password is long enough
  let passwordErrorText = checkValidPassword(password);
  if (passwordErrorText) {
    yield put(setPasswordErrorText(passwordErrorText));
  }

  // Check if phone number is long enough
  let phoneNumberErrorText = checkValidPhoneNumber(phoneNumber);
  if (phoneNumberErrorText) {
    yield put(setPhoneNumberErrorText(phoneNumberErrorText));
  }


  // If no input errors were hit, dispatch the API call
  if (checkInputErrors(missingInputsList,
    emailErrorText, passwordErrorText,
    phoneNumberErrorText)) {
    try {
      // Clear Cookies (Logout)
      yield call(clearCookiesRequest);

      // Create Account
      yield call(
        createAccountRequest,
        email,
        firstName,
        lastName,
        password,
        phoneNumber,
        countryCode
      );

      // analytics for registration success
      registrationSuccess(firstName, lastName, email);

      // Log user in
      const loginResponse = yield call(loginRequest, email, password);
      const userId = loginResponse.user.id;

      // Eula Agreement
      yield call(agreementDocumentRequest, userId, eulaId, navigator.userAgent);

      // Privacy Policy Agreement
      yield call(agreementDocumentRequest, userId, privacyPolicyId, navigator.userAgent);

      // Mark account creation successful
      yield put(createAccountSuccess(loginResponse.user));

      // Reroute user
      return yield call(redirectUser);

    } catch (error) {
      const errorType = errorHandler(error);
      switch (errorType) {
        case 'emailAlreadyExist':
          yield put(setEmailErrorText('emailAlreadyExists'));
          break;
        case 'invalidEmail':
          yield put(setEmailErrorText('invalidEmail'));
          break;
        case 'password':
          yield put(setPasswordErrorText('password'));
          break;
        default:
          return yield put(createAccountFailure('apiError'));
      }
    }
  }
  // Mark account creation failed
  return yield put(createAccountFailure(''));
}

/**
 * Create Account API call.
 * On success, handle rerouting the user correctly
 * On failure, dispatch createAccountFailure with the response error code
 * @param {String} countryId
 * @return {void}
 */
export function *setEulaAndPrivatePolicy(countryId) {
  try {
    const response = yield call(eulaRequest, countryId);
    yield put(setEulaLink(response));
    const privacyPolicyResponse = yield call(privacyPolicyRequest, countryId);
    yield put(setPrivacyPolicy(privacyPolicyResponse));
    return yield put(setEulaLinkSuccess());

  } catch (error) {
    return yield put(setEulaLinkFailure(error));
  }
}

/**
 * Find user's location through Google API call.
 * On success, dispatch action to set country, PP and EULA on registration page
 * On failure, dispatch setCountryFailure with the response error code
 * @param {Object} coordinates
 * @return {void}
 */
export function *getCountryByLocation(coordinates) {
  try {
    const response = yield call(locationRequest, coordinates);

    if (response.countryCode) {
      return yield put(setCountry(null, response.countryCode));
    }
    return yield put(setCountryFailure());
  } catch (error) {
    return yield put(setCountryFailure());
  }
}

export function *processCreateAccount() {
  const state = yield select();
  const {
    email,
    firstName,
    isEulaAccepted,
    lastName,
    password,
    phoneNumber,
    eulaId,
    privacyPolicyId,
    country,
    countryCode
  } = state.registration;

  yield call(
    createAccount, email, firstName.trim(), isEulaAccepted, lastName.trim(),
    password, phoneNumber, eulaId, privacyPolicyId, country, countryCode
  );
}

/**
 * Waits for the return of the CREATE_ACCOUNT_REQUEST action event, pulls registration values
 * from redux state, and calls the createAccount saga
 * @return {void}
 */
export function *createAccountWatcher() {
  yield* takeEvery(CREATE_ACCOUNT_REQUEST, processCreateAccount);
}

export function *processSetCountry({ countryId }) {
  yield call(setEulaAndPrivatePolicy, countryId);
}

export function *setCountryWatcher() {
  yield* takeEvery(SET_COUNTRY_REQUEST, processSetCountry);
}

export function *processSetCountryByLocation({ coordinates }) {
  yield call(getCountryByLocation, coordinates);
}

export function *setCountryByLocationWatcher() {
  yield* takeEvery(SET_COUNTRY_BY_LOCATION_API, processSetCountryByLocation);
}

/**
 * Uses fork to start multiple watchers in parallel
 * @return {void}
 */
export default function *rootSaga() {
  yield fork(createAccountWatcher);
  yield fork(setCountryWatcher);
  yield fork(setCountryByLocationWatcher);
}
